// Real HTTP backend pointing at the @osmix/api-gateway service. The
// admin shares the gateway with seller-web; the dispatcher in
// ./client.ts decides whether to use this implementation or ./mock/
// based on `BUN_PUBLIC_API_GATEWAY_URL` being set at bundle time.

import { ApiClientError, createApiClient, type ApiClient } from "@osmix/api-client";
import { ApiError } from "./errors";
import {
	NotificationCampaignPageSchema,
	NotificationCampaignPreviewSchema,
	NotificationCampaignSchema,
	NotificationCampaignStatsSchema,
	NotificationItemSchema,
	NotificationPageSchema,
	NotificationRecipientPageSchema,
	NotificationUnreadCountSchema,
	WeeklyDigestSettingsSchema,
} from "@osmix/contracts/notification";
// Импорт из корневого barrel @osmix/contracts, а не из subpath
// @osmix/contracts/seller-document: dev-бандлер резолвит subpath-exports по
// снимку из lockfile (снятому до появления экспорта seller-document), тогда как
// реэкспорт внутри barrel резолвится по относительному пути и работает всегда.
import {
	SellerDocumentListSchema,
	SellerDocumentSchema,
	SellerVerificationSchema,
	GeoFlowAnalyticsSchema,
	FaqArticleSchema,
	SupportTicketSchema,
	SupportTicketDetailSchema,
} from "@osmix/contracts";
import type {
	CatalogImportFile,
	CatalogImportItem,
	CatalogImportJob,
	CatalogImportJobDetail,
	CatalogImportModels,
	CrmActivity,
	CrmContact,
	CrmDuplicate,
	CrmFile,
	CrmLeadImportResult,
	CrmTask,
	CrmTaskWithLead,
	MetrikaReport,
} from "@osmix/contracts";
import type { ApplicationCard } from "./types";
import { getAuthToken, reportUnauthorized } from "./token";
import {
	CommissionCategoryShops,
	CommissionHistoryEntry,
	CommissionOverview,
	CommissionShopList,
} from "./types";
import {
	type Api,
	type ApplicationBoard,
	type AuditEntry,
	type AuditFacets,
	type AuditPage,
	type IntegrationEvent,
	type PartnerApiKey,
	type IntegrationEventSummary,
	type ApplicationReworkInput,
	type ApplicationReworkResult,
	AuthSession,
	AuthUser,
	Cart,
	type DashboardOverview,
	type DashboardRange,
	type FinanceSummary,
	type PromoCode,
	type PromoCodeInput,
	type PromoStats,
	type PromoCheckResult,
	type PartnerSettings,
	type SellerPartnerPromo,
	type Partner,
	type PartnerDetail,
	type ObligationDocument,
	type ShopPartners,
	type ShopPickupPoints,
	type ObligationState,
	type SellerApplication,
	Category,
	CategoryAttributeSchema,
	type CategoryDeleteImpact,
	type CategoryInput,
	type ProductBulkResult,
	type CategoryCompliance,
	type ComplianceAdminSummary,
	type ComplianceDocument,
	type ComplianceDocumentProduct,
	type ComplianceJournalPage,
	type ComplianceMatrixRow,
	type ComplianceQueuePage,
	type ComplianceReport,
	type ProductCompliance,
	type CreateFeedBlockRequest,
	type Credentials,
	type FeedBlockAdmin,
	type FeedPlacement,
	type PersonalFeedSettings,
	type PersonalFeedSettingsPatch,
	type UpdateFeedBlockRequest,
	CarrierAdmin,
	CarrierCheck,
	DeliveryConfig,
	type DeliveryConfigPatch,
	SmsChannelHealth,
	SmsLogPage,
	EmailChannelStatus,
	EmailLogPage,
	EmailMessageDetail,
	EmailSendOutcome,
	EmailTemplateMeta,
	EmailTemplatePreview,
	type EmailLogFilter,
	type SmsLogFilter,
	SmsTestResult,
	GeoCountry,
	GeoCurrency,
	Idea,
	type IdeaFilter,
	InspirationFeedItem,
	type InspirationFeedItemInput,
	InspirationIdeaFeedItem,
	Order,
	OrderReceipt,
	OrderShipment,
	ReturnDetail,
	ReturnRequest,
	type ReturnResolveInput,
	type ReturnStatus,
	ReturnsSummary,
	Product,
	type ProductFilter,
	ProductFilterOptions,
	type ProductIds,
	type ProductQuery,
	type ServerPage,
	type IdeaListSlice,
	ProductPage,
	GeneratedReport,
	ReportCatalogEntry,
	ReportHistoryEntry,
	FinanceSettings,
	PayoutDetails,
	PayoutRequest,
	type PaymentRegistry,
	type CashFlowReport,
	type AccountingJournalEntry,
	type AccountingQuery,
	type AccountingReturnsSummary,
	type AccountingSalesSummary,
	type AccountingShop,
	type AgentReportSummary,
	type CommissionActSummary,
	type SettlementsReport,
	type ApplicationAccountResult,
	IntegrationCheckResult,
	IntegrationsOverview,
	InfrastructureCosts,
	ModerationSettings,
	PriceRequest,
	PriceRequestPage,
	ProductStatusCounts,
	RejectionReason,
	PlatformPermission,
	PlatformRole,
	SellerShop,
	SellerShopDetail,
	ProfileSectionState,
	ShopMember,
	ShopStaff,
	ShopAnalytics,
	ShopSalesSummary,
	Storefront,
	User,
	type BuyerAnalytics,
	type TopBuyer,
	type TopBuyerMetric,
	type PersonalOffersOverview,
	type PersonalOffersRunResult,
	type PersonalOffersSettings,
} from "./types";
import { createPromoCampaignsHttp } from "./promo-campaigns";
import { createUgcHttp } from "./ugc-articles";

type Envelope<T> =
	| { success: true; data: T }
	| {
			success: false;
			error: { code: string; message: string; details?: unknown };
	  };

const buildUrl = (
	baseUrl: string,
	path: string,
	query?: Record<string, string | number | undefined>,
): string => {
	const url = new URL(path.replace(/^\//, ""), baseUrl.replace(/\/?$/, "/"));
	if (query) {
		for (const [k, v] of Object.entries(query)) {
			if (v === undefined) continue;
			url.searchParams.set(k, String(v));
		}
	}
	return url.toString();
};

/** Срез списка товаров в query: пустые поля не отправляем. */
const productQuery = (q: ProductQuery): Record<string, string | undefined> => ({
	status: q.status,
	search: q.search?.trim() || undefined,
	shopId: q.shopId || undefined,
	categoryId: q.categoryId || undefined,
	teamLeadId: q.teamLeadId || undefined,
	awaiting: q.awaiting ? "1" : undefined,
	compliance: q.compliance || undefined,
	dateField: q.from || q.to ? q.dateField : undefined,
	from: q.from || undefined,
	to: q.to || undefined,
	sort: q.sort,
	dir: q.sort ? q.dir : undefined,
});

// Разбираем заказы по одному: строгий `Order.array()` ронял весь раздел из-за
// одного заказа с непредвиденной формой (живая оплата, старая запись). Такой
// заказ пропускаем и называем в консоли — остальные видны.
const parseOrders = (raw: unknown): Order[] => {
	if (!Array.isArray(raw)) return Order.array().parse(raw);
	const orders: Order[] = [];
	for (const item of raw) {
		const parsed = Order.safeParse(item);
		if (parsed.success) {
			orders.push(parsed.data);
		} else {
			const id = (item as { id?: unknown } | null)?.id;
			console.error(`[orders] заказ ${String(id)} не прошёл разбор`, parsed.error.issues);
		}
	}
	return orders;
};

const clients = new Map<string, ApiClient>();

const clientFor = (baseUrl: string): ApiClient => {
	const existing = clients.get(baseUrl);
	if (existing) return existing;
	const client = createApiClient({ baseUrl, getBearerToken: getAuthToken });
	clients.set(baseUrl, client);
	return client;
};

const request = async <T>(
	baseUrl: string,
	path: string,
	opts: {
		method?: string;
		query?: Record<string, string | number | undefined>;
		body?: unknown;
	} = {},
): Promise<T> => {
	const sentWith = getAuthToken();
	try {
		const payload = await clientFor(baseUrl).request<Envelope<T> | undefined>(path, opts);
		if (payload === undefined) return undefined as T;
		if (payload.success) return payload.data;
		const err = payload?.success === false ? payload.error : null;
		throw new ApiError(err?.message ?? "Request failed", 400, err?.code, err?.details);
	} catch (error) {
		if (error instanceof ApiError) throw error;
		if (error instanceof ApiClientError) {
			if (error.status === 401) reportUnauthorized(sentWith);
			const payload = error.body as Envelope<T> | null;
			const err = payload?.success === false ? payload.error : null;
			throw new ApiError(
				err?.message ?? `Request failed: ${error.status}`,
				error.status,
				err?.code,
				err?.details,
			);
		}
		throw error;
	}
};

// Binary sibling of `request`: file routes stream raw bytes (no JSON envelope),
// so this carries the same bearer auth but returns the response Blob — the
// browser can't attach an Authorization header to <iframe>/<img>/window.open,
// so authenticated files are fetched here and handed out as object URLs.
const requestBlob = async (baseUrl: string, path: string): Promise<Blob> => {
	const headers: Record<string, string> = {};
	const token = getAuthToken();
	if (token) headers["Authorization"] = `Bearer ${token}`;
	const res = await fetch(buildUrl(baseUrl, path), { headers });
	if (!res.ok) throw new ApiError(`Request failed: ${res.status}`, res.status);
	return res.blob();
};

/**
 * Файл выгрузки с именем и счётчиками из заголовков. Ошибка шлюза приходит
 * JSON-конвертом — её текст («не заданы реквизиты площадки…») показываем
 * человеку как есть, а не «Request failed: 400».
 */
/** Фильтр бухгалтерии в query: пустые поля не отправляем. */
const accountingQuery = (query: AccountingQuery): Record<string, string> => {
	const out: Record<string, string> = {};
	for (const [key, value] of Object.entries(query)) if (value) out[key] = value;
	return out;
};

const requestFile = async (
	baseUrl: string,
	path: string,
	fallbackName: string,
): Promise<{ blob: Blob; filename: string; rows: number; skipped: number }> => {
	const headers: Record<string, string> = {};
	const token = getAuthToken();
	if (token) headers["Authorization"] = `Bearer ${token}`;
	const res = await fetch(buildUrl(baseUrl, path), { headers });
	if (!res.ok) {
		let message = `Request failed: ${res.status}`;
		try {
			const body = (await res.json()) as { error?: { message?: string } };
			if (body?.error?.message) message = body.error.message;
		} catch {
			// тело не JSON — остаётся код ответа
		}
		throw new ApiError(message, res.status);
	}
	const disposition = res.headers.get("content-disposition") ?? "";
	const match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
	let filename = fallbackName;
	if (match?.[1]) {
		try {
			filename = decodeURIComponent(match[1]);
		} catch {
			// битое имя — берём запасное
		}
	}
	return {
		blob: await res.blob(),
		filename,
		rows: Number(res.headers.get("x-export-rows") ?? 0),
		skipped: Number(res.headers.get("x-export-skipped") ?? 0),
	};
};

// Text sibling of `requestBlob`: the reports HTML route streams a raw document
// (Content-Type text/html, no JSON envelope). Carries the same bearer auth so a
// stored report can be re-fetched and handed to an <iframe srcDoc>.
const requestText = async (baseUrl: string, path: string): Promise<string> => {
	const headers: Record<string, string> = {};
	const token = getAuthToken();
	if (token) headers["Authorization"] = `Bearer ${token}`;
	const res = await fetch(buildUrl(baseUrl, path), { headers });
	if (!res.ok) throw new ApiError(`Request failed: ${res.status}`, res.status);
	return res.text();
};

// Заголовок X-Filename должен быть ASCII (кириллица/пробелы в имени файла ломают
// заголовок), поэтому имя санитизируется: небуквенно-цифровые символы → «_».
const asciiFilename = (name: string): string =>
	name.replace(/[^\w.-]+/g, "_").replace(/^_+|_+$/g, "") || "upload";

// Upload-sibling of `requestBlob`: raw-binary upload routes (feed media, idea
// photos) read the file straight from the request body with the original name in
// X-Filename and mime in Content-Type — that is what the gateway's readUpload
// expects. Carries the same bearer auth and unwraps the `{ url }` envelope.
const requestUpload = async (
	baseUrl: string,
	path: string,
	file: File,
): Promise<{ url: string }> => {
	const headers: Record<string, string> = {
		"X-Filename": asciiFilename(file.name),
		"Content-Type": file.type || "application/octet-stream",
	};
	const token = getAuthToken();
	if (token) headers["Authorization"] = `Bearer ${token}`;
	const res = await fetch(buildUrl(baseUrl, path), {
		method: "POST",
		headers,
		body: file,
	});
	const payload = (await res
		.json()
		.catch(() => null)) as Envelope<{ url: string }> | null;
	if (res.ok && payload?.success) return payload.data;
	const err = payload?.success === false ? payload.error : null;
	throw new ApiError(err?.message ?? `Upload failed: ${res.status}`, res.status, err?.code);
};

/**
 * Диапазон дашборда в query-параметры.
 *
 * Значение по умолчанию на бэкенде — «С первого дня», поэтому allTime
 * передаётся явно: пресет по умолчанию не должен зависеть от того, что
 * подставит сервер, если параметр потеряется по дороге.
 */
const rangeQuery = (range: DashboardRange): Record<string, string | number | undefined> => {
	if (range.kind === "preset") return { kind: "preset", days: range.days };
	if (range.kind === "custom") return { kind: "custom", from: range.from, to: range.to };
	return { kind: "allTime" };
};

/** Placeholder for an `Api` method the gateway does not implement yet. */
const notImplemented =
	(operation: string) =>
	(): never => {
		throw new ApiError(
			`${operation} has no gateway endpoint yet — run it against the mock provider (BUN_PUBLIC_MOCK_DOMAINS)`,
			501,
			"NOT_IMPLEMENTED",
		);
	};

/**
 * multipart-сестра `requestUpload`: файл уходит полем `file` вместе с полями
 * формы, имя файла — как есть (кириллица в multipart не ломается, в отличие от
 * заголовка X-Filename). Для материалов заявки и ИИ-загрузки каталога.
 */
const requestForm = async <T>(
	baseUrl: string,
	path: string,
	file: File,
	fields: Record<string, string> = {},
): Promise<T> => {
	const form = new FormData();
	form.append("file", file, file.name);
	for (const [key, value] of Object.entries(fields)) form.append(key, value);
	const headers: Record<string, string> = {};
	const token = getAuthToken();
	if (token) headers["Authorization"] = `Bearer ${token}`;
	const res = await fetch(buildUrl(baseUrl, path), { method: "POST", headers, body: form });
	const payload = (await res.json().catch(() => null)) as Envelope<T> | null;
	if (res.ok && payload?.success) return payload.data;
	const err = payload?.success === false ? payload.error : null;
	throw new ApiError(err?.message ?? `Upload failed: ${res.status}`, res.status, err?.code);
};

export const createHttp = (baseUrl: string): Api => ({
	promoCampaigns: createPromoCampaignsHttp((path, opts) => request(baseUrl, path, opts)),
	ugc: createUgcHttp((path, opts) => request(baseUrl, path, opts)),
	auth: {
		login: async (credentials: Credentials) =>
			AuthSession.parse(
				await request<unknown>(baseUrl, "/auth/admin-login", {
					method: "POST",
					body: credentials,
				}),
			),
		me: async () => AuthUser.parse(await request<unknown>(baseUrl, "/auth/me")),
		logout: async () => {
			await request<null>(baseUrl, "/auth/logout", { method: "POST" });
		},
	},
	products: {
		list: async (filter: ProductFilter) => {
			const data = await request<unknown>(baseUrl, "/admin/products", {
				query: { ...productQuery(filter), page: filter.page, pageSize: filter.pageSize },
			});
			return ProductPage.parse(data);
		},
		ids: async (query) =>
			(await request<ProductIds>(baseUrl, "/admin/products/ids", {
				query: productQuery(query),
			})) as ProductIds,
		filterOptions: async () =>
			ProductFilterOptions.parse(
				await request<unknown>(baseUrl, "/admin/products/filter-options"),
			),
		get: async (id) =>
			Product.parse(
				await request<unknown>(
					baseUrl,
					`/admin/products/${encodeURIComponent(id)}`,
				),
			),
		approve: async (id) =>
			Product.parse(
				await request<unknown>(
					baseUrl,
					`/admin/products/${encodeURIComponent(id)}/approve`,
					{
						method: "POST",
					},
				),
			),
		priceRequests: async (filter) =>
			PriceRequestPage.parse(
				await request<unknown>(baseUrl, "/admin/price-requests", {
					query: {
						status: filter.status ?? "pending",
						page: filter.page,
						pageSize: filter.pageSize,
					},
				}),
			),
		approvePriceRequest: async (id) =>
			PriceRequest.parse(
				await request<unknown>(
					baseUrl,
					`/admin/price-requests/${encodeURIComponent(id)}/approve`,
					{ method: "POST" },
				),
			),
		rejectPriceRequest: async (id, reason) =>
			PriceRequest.parse(
				await request<unknown>(
					baseUrl,
					`/admin/price-requests/${encodeURIComponent(id)}/reject`,
					{ method: "POST", body: { reason } },
				),
			),
		statusCounts: async (query = {}) =>
			ProductStatusCounts.parse(
				await request<unknown>(baseUrl, "/admin/products/status-counts", {
					query: productQuery(query),
				}),
			),
		rejectionReasons: async () =>
			RejectionReason.array().parse(
				await request<unknown>(baseUrl, "/admin/products/rejection-reasons"),
			),
		reject: async (id, input) =>
			Product.parse(
				await request<unknown>(
					baseUrl,
					`/admin/products/${encodeURIComponent(id)}/reject`,
					{
						method: "POST",
						// Передаём код типовой причины и/или свободный комментарий.
						body: { reasonCode: input?.reasonCode, reason: input?.reason },
					},
				),
			),
		block: async (id, reason) =>
			Product.parse(
				await request<unknown>(baseUrl, `/admin/products/${encodeURIComponent(id)}/block`, {
					method: "POST",
					body: { reason },
				}),
			),
		bulk: async (input) =>
			(await request<ProductBulkResult>(baseUrl, "/admin/products/bulk", {
				method: "POST",
				body: input,
			})) as ProductBulkResult,
		setFacets: async (id, input) => {
			await request<unknown>(baseUrl, `/admin/products/${encodeURIComponent(id)}/facets`, {
				method: "PUT",
				body: input,
			});
		},
		moveCategory: async (id, categoryId) => {
			await request<unknown>(baseUrl, `/admin/products/${encodeURIComponent(id)}/category`, {
				method: "PUT",
				body: { categoryId },
			});
		},
	},
	users: {
		list: async () =>
			User.array().parse(await request<unknown>(baseUrl, "/admin/users")),
		page: async (filter) => {
			const raw = await request<ServerPage<unknown>>(baseUrl, "/admin/users", {
				query: {
					search: filter.search?.trim() || undefined,
					role: filter.role || undefined,
					blocked: filter.blocked ? "1" : undefined,
					sort: filter.sort,
					dir: filter.sort ? filter.dir : undefined,
					page: filter.page,
					pageSize: filter.pageSize,
				},
			});
			return { ...raw, items: User.array().parse(raw.items) };
		},
		create: async (input) =>
			User.parse(
				await request<unknown>(baseUrl, "/admin/users", {
					method: "POST",
					body: input,
				}),
			),
		get: async (id) =>
			User.parse(
				await request<unknown>(
					baseUrl,
					`/admin/users/${encodeURIComponent(id)}`,
				),
			),
		update: async (id, patch) =>
			User.parse(
				await request<unknown>(
					baseUrl,
					`/admin/users/${encodeURIComponent(id)}`,
					{
						method: "PATCH",
						body: patch,
					},
				),
			),
		block: async (id, reason) =>
			User.parse(
				await request<unknown>(baseUrl, `/admin/users/${encodeURIComponent(id)}/block`, {
					method: "POST",
					body: { reason },
				}),
			),
		unblock: async (id) =>
			User.parse(
				await request<unknown>(baseUrl, `/admin/users/${encodeURIComponent(id)}/unblock`, {
					method: "POST",
				}),
			),
		changePassword: async (id, input) =>
			User.parse(
				await request<unknown>(
					baseUrl,
					`/admin/users/${encodeURIComponent(id)}/password`,
					{
						method: "POST",
						body: input,
					},
				),
			),
		staff: async (id) =>
			ShopMember.array().parse(
				await request<unknown>(
					baseUrl,
					`/admin/users/${encodeURIComponent(id)}/staff`,
				),
			),
		buyerAnalytics: async (id: string) =>
			(await request<BuyerAnalytics>(
				baseUrl,
				`/admin/users/${encodeURIComponent(id)}/buyer-analytics`,
			)) as BuyerAnalytics,
		// Топ-покупатели: рейтинг за окно по сумме трат или числу заказов. Ответ
		// приходит в конверте `{ items }`; сам TopBuyer — сервисный тип без
		// zod-схемы в contracts, поэтому возвращаем его как есть (типизация — по
		// сигнатуре Api).
		topBuyers: async (input: { metric: TopBuyerMetric; days?: number; limit?: number }) =>
			(await request<{ items: TopBuyer[] }>(baseUrl, "/admin/buyers/top", {
				query: { metric: input.metric, days: input.days, limit: input.limit },
			})) as { items: TopBuyer[] },
	},
	notifications: {
		list: async (input) =>
			NotificationPageSchema.parse(
				await request<unknown>(baseUrl, "/notifications", {
					query: {
						cursor: input.cursor,
						limit: input.limit,
						filter: input.filter,
						locale: input.locale,
					},
				}),
			),
		detail: async (id, locale) =>
			NotificationItemSchema.parse(
				await request<unknown>(baseUrl, `/notifications/${encodeURIComponent(id)}`, {
					query: { locale },
				}),
			),
		unreadCount: async () =>
			NotificationUnreadCountSchema.parse(
				await request<unknown>(baseUrl, "/notifications/unread-count"),
			),
		markRead: async (id) => {
			await request<null>(baseUrl, `/notifications/${encodeURIComponent(id)}/read`, { method: "POST" });
		},
		markAllRead: async () => {
			await request<null>(baseUrl, "/notifications/read-all", { method: "POST" });
		},
	},
	notificationCampaigns: {
		list: async (input = {}) =>
			NotificationCampaignPageSchema.parse(
				await request<unknown>(baseUrl, "/admin/notification-campaigns", { query: input }),
			),
		get: async (id) =>
			NotificationCampaignSchema.parse(
				await request<unknown>(baseUrl, `/admin/notification-campaigns/${encodeURIComponent(id)}`),
			),
		create: async (input) =>
			NotificationCampaignSchema.parse(
				await request<unknown>(baseUrl, "/admin/notification-campaigns", { method: "POST", body: input }),
			),
		update: async (id, input) =>
			NotificationCampaignSchema.parse(
				await request<unknown>(baseUrl, `/admin/notification-campaigns/${encodeURIComponent(id)}`, { method: "PATCH", body: input }),
			),
		preview: async (id, locale = "ru") =>
			NotificationCampaignPreviewSchema.parse(
				await request<unknown>(baseUrl, `/admin/notification-campaigns/${encodeURIComponent(id)}/preview`, { method: "POST", body: { locale } }),
			),
		send: async (id, input) =>
			NotificationCampaignSchema.parse(
				await request<unknown>(baseUrl, `/admin/notification-campaigns/${encodeURIComponent(id)}/send`, { method: "POST", body: input }),
			),
		cancel: async (id) =>
			NotificationCampaignSchema.parse(
				await request<unknown>(baseUrl, `/admin/notification-campaigns/${encodeURIComponent(id)}/cancel`, { method: "POST" }),
			),
		stats: async (id) =>
			NotificationCampaignStatsSchema.parse(
				await request<unknown>(baseUrl, `/admin/notification-campaigns/${encodeURIComponent(id)}/stats`),
			),
		recipients: async (input = {}) =>
			NotificationRecipientPageSchema.parse(
				await request<unknown>(baseUrl, "/admin/notification-recipients", { query: input }),
			),
		weeklyDigest: async () =>
			WeeklyDigestSettingsSchema.parse(await request<unknown>(baseUrl, "/admin/notifications/weekly-digest")),
		updateWeeklyDigest: async (input) =>
			WeeklyDigestSettingsSchema.parse(
				await request<unknown>(baseUrl, "/admin/notifications/weekly-digest", { method: "PUT", body: input }),
			),
	},
	platformRoles: {
		catalog: async () =>
			PlatformPermission.array().parse(
				await request<unknown>(baseUrl, "/admin/platform-permissions/catalog"),
			),
		list: async () =>
			PlatformRole.array().parse(await request<unknown>(baseUrl, "/admin/platform-roles")),
		create: async (input) =>
			PlatformRole.parse(
				await request<unknown>(baseUrl, "/admin/platform-roles", {
					method: "POST",
					body: input,
				}),
			),
		update: async (key, patch) =>
			PlatformRole.parse(
				await request<unknown>(baseUrl, `/admin/platform-roles/${encodeURIComponent(key)}`, {
					method: "PATCH",
					body: patch,
				}),
			),
		remove: async (key) => {
			await request<null>(baseUrl, `/admin/platform-roles/${encodeURIComponent(key)}`, {
				method: "DELETE",
			});
		},
	},
	compliance: {
		summary: async () =>
			(await request<ComplianceAdminSummary>(baseUrl, "/admin/compliance/summary")) as ComplianceAdminSummary,
		unpublishMissing: async () =>
			(await request<{ count: number; shops: number }>(baseUrl, "/admin/compliance/unpublish-missing", {
				method: "POST",
			})) as { count: number; shops: number },
		applyRecommended: async () =>
			(await request<{ updated: string[] }>(baseUrl, "/admin/compliance/categories/recommended", {
				method: "POST",
			})) as { updated: string[] },
		setCategory: async (id, input) =>
			(await request<CategoryCompliance>(baseUrl, `/admin/categories/${encodeURIComponent(id)}/compliance`, {
				method: "PUT",
				body: input,
			})) as CategoryCompliance,
		rejectDocument: async (id, reason) =>
			(await request<ComplianceDocument>(baseUrl, `/admin/compliance-documents/${encodeURIComponent(id)}/reject`, {
				method: "POST",
				body: { reason },
			})) as ComplianceDocument,
		documentFile: (id) => requestBlob(baseUrl, `/admin/compliance-documents/${encodeURIComponent(id)}/file`),
		listDocuments: async (query) =>
			(await request<ComplianceQueuePage>(baseUrl, "/admin/compliance-documents", {
				query: { status: query.status, search: query.search || undefined, page: query.page, pageSize: query.pageSize },
			})) as ComplianceQueuePage,
		getDocument: async (id) =>
			(await request<ComplianceDocument>(baseUrl, `/admin/compliance-documents/${encodeURIComponent(id)}`)) as ComplianceDocument,
		documentProducts: async (id) =>
			(await request<ComplianceDocumentProduct[]>(
				baseUrl,
				`/admin/compliance-documents/${encodeURIComponent(id)}/products`,
			)) as ComplianceDocumentProduct[],
		approveDocument: async (id, input) =>
			(await request<ComplianceDocument>(baseUrl, `/admin/compliance-documents/${encodeURIComponent(id)}/approve`, {
				method: "POST",
				body: input,
			})) as ComplianceDocument,
		admit: async (productId, input) =>
			(await request<ProductCompliance>(baseUrl, `/admin/products/${encodeURIComponent(productId)}/compliance-admission`, {
				method: "POST",
				body: input,
			})) as ProductCompliance,
		revokeAdmission: async (productId, reason) =>
			(await request<ProductCompliance>(
				baseUrl,
				`/admin/products/${encodeURIComponent(productId)}/compliance-admission/revoke`,
				{ method: "POST", body: reason ? { reason } : {} },
			)) as ProductCompliance,
		report: async (query = {}) =>
			(await request<ComplianceReport>(baseUrl, "/admin/compliance/report", { query: { ...query } })) as ComplianceReport,
		reportCsv: async (query = {}) => {
			const params = new URLSearchParams();
			for (const [key, value] of Object.entries(query)) if (value) params.set(key, value);
			const suffix = params.toString() ? `?${params}` : "";
			const file = await requestFile(baseUrl, `/admin/compliance/report.csv${suffix}`, "Товары без документа.csv");
			return { blob: file.blob, filename: file.filename };
		},
		journal: async (query = {}) =>
			(await request<ComplianceJournalPage>(baseUrl, "/admin/compliance/journal", {
				query: { page: query.page, pageSize: query.pageSize, entityId: query.entityId },
			})) as ComplianceJournalPage,
		matrix: async () => (await request<ComplianceMatrixRow[]>(baseUrl, "/admin/compliance/matrix")) as ComplianceMatrixRow[],
	},
	categories: {
		list: async () =>
			Category.array().parse(
				await request<unknown>(baseUrl, "/admin/categories"),
			),
		create: async (input: CategoryInput) =>
			Category.parse(
				await request<unknown>(baseUrl, "/admin/categories", {
					method: "POST",
					body: input,
				}),
			),
		update: async (id, patch) =>
			Category.parse(
				await request<unknown>(
					baseUrl,
					`/admin/categories/${encodeURIComponent(id)}`,
					{
						method: "PATCH",
						body: patch,
					},
				),
			),
		remove: async (id, moveTo) => {
			await request<null>(
				baseUrl,
				`/admin/categories/${encodeURIComponent(id)}`,
				{
					method: "DELETE",
					query: { moveTo },
				},
			);
		},
		deleteImpact: async (id) =>
			(await request<CategoryDeleteImpact>(
				baseUrl,
				`/admin/categories/${encodeURIComponent(id)}/delete-impact`,
			)) as CategoryDeleteImpact,
		setFacets: async (id, input) => {
			await request<unknown>(baseUrl, `/admin/categories/${encodeURIComponent(id)}/facets`, {
				method: "PUT",
				body: input,
			});
		},
		attributeSchema: async (id) =>
			CategoryAttributeSchema.parse(
				await request<unknown>(baseUrl, `/admin/categories/${encodeURIComponent(id)}/attribute-schema`),
			),
		updateAttributeSchema: async (id, attributes) =>
			CategoryAttributeSchema.parse(
				await request<unknown>(baseUrl, `/admin/categories/${encodeURIComponent(id)}/attribute-schema`, {
					method: "PUT",
					body: { attributes },
				}),
			),
		resetAttributeSchema: async (id) => {
			await request<unknown>(baseUrl, `/admin/categories/${encodeURIComponent(id)}/attribute-schema`, {
				method: "DELETE",
			});
		},
		sectionCounts: async () =>
			(await request<{ section: string; count: number }[]>(baseUrl, "/admin/catalog/section-counts")) as {
				section: string;
				count: number;
			}[],
	},
	inspirationFeed: {
		list: async () =>
			InspirationFeedItem.array().parse(
				await request<unknown>(baseUrl, "/admin/inspiration-feed"),
			),
		add: async (input: InspirationFeedItemInput) =>
			InspirationFeedItem.parse(
				await request<unknown>(baseUrl, "/admin/inspiration-feed", {
					method: "POST",
					body: input,
				}),
			),
		update: async (id, patch) =>
			InspirationFeedItem.parse(
				await request<unknown>(
					baseUrl,
					`/admin/inspiration-feed/${encodeURIComponent(id)}`,
					{
						method: "PATCH",
						body: patch,
					},
				),
			),
		reorder: async (ids) =>
			InspirationFeedItem.array().parse(
				await request<unknown>(baseUrl, "/admin/inspiration-feed/reorder", {
					method: "POST",
					body: { ids },
				}),
			),
		remove: async (id) => {
			await request<null>(
				baseUrl,
				`/admin/inspiration-feed/${encodeURIComponent(id)}`,
				{
					method: "DELETE",
				},
			);
		},
	},
	inspirationIdeas: {
		list: async () =>
			InspirationIdeaFeedItem.array().parse(
				await request<unknown>(baseUrl, "/admin/inspiration-ideas"),
			),
		reorder: async (ids) =>
			InspirationIdeaFeedItem.array().parse(
				await request<unknown>(baseUrl, "/admin/inspiration-ideas/reorder", {
					method: "POST",
					body: { ids },
				}),
			),
	},
	// Единая управляемая выдача главной (fd-feed-01). Списки приходят в конверте
	// `{ blocks }`; сам блок — плоский DTO без zod-схемы в contracts, поэтому
	// возвращаем его как есть (типизация — через сигнатуру Api).
	feed: {
		list: async (placement: FeedPlacement) => {
			const data = await request<{ blocks: FeedBlockAdmin[] }>(baseUrl, "/admin/feed/blocks", {
				query: { placement },
			});
			return data.blocks;
		},
		create: async (input: CreateFeedBlockRequest) =>
			(await request<FeedBlockAdmin>(baseUrl, "/admin/feed/blocks", {
				method: "POST",
				body: input,
			})) as FeedBlockAdmin,
		update: async (id: string, input: UpdateFeedBlockRequest) =>
			(await request<FeedBlockAdmin>(
				baseUrl,
				`/admin/feed/blocks/${encodeURIComponent(id)}`,
				{ method: "PUT", body: input },
			)) as FeedBlockAdmin,
		remove: async (id: string) => {
			await request<{ ok: true }>(baseUrl, `/admin/feed/blocks/${encodeURIComponent(id)}`, {
				method: "DELETE",
			});
		},
		reorder: async (placement: FeedPlacement, orderedIds: string[]) => {
			const data = await request<{ blocks: FeedBlockAdmin[] }>(
				baseUrl,
				"/admin/feed/blocks/reorder",
				{ method: "POST", body: { placement, orderedIds } },
			);
			return data.blocks;
		},
		// Загрузка своего изображения слайда/плитки: сырой бинарный upload, ответ —
		// публичный URL, который кладётся в imageUrl строки.
		uploadMedia: async (file: File) =>
			(await requestUpload(baseUrl, "/admin/feed/media", file)).url,
		personalSettings: async () =>
			await request<PersonalFeedSettings>(baseUrl, "/admin/feed/personal-settings"),
		updatePersonalSettings: async (patch: PersonalFeedSettingsPatch) =>
			await request<PersonalFeedSettings>(baseUrl, "/admin/feed/personal-settings", {
				method: "PUT",
				body: patch,
			}),
	},
	ideas: {
		page: async (filter) => {
			const raw = await request<ServerPage<unknown>>(baseUrl, "/admin/ideas", {
				query: {
					list: filter.list,
					shopId: filter.shopId || undefined,
					dir: filter.dir,
					page: filter.page,
					pageSize: filter.pageSize,
				},
			});
			return { ...raw, items: Idea.array().parse(raw.items) };
		},
		counts: async (shopId) =>
			(await request<Record<IdeaListSlice, number>>(baseUrl, "/admin/ideas/counts", {
				query: { shopId: shopId || undefined },
			})) as Record<IdeaListSlice, number>,
		shops: async () =>
			(await request<{ id: string; name: string }[]>(baseUrl, "/admin/ideas/shops")) as {
				id: string;
				name: string;
			}[],
		list: async (filter: IdeaFilter) =>
			Idea.array().parse(
				await request<unknown>(baseUrl, "/admin/ideas", {
					query: { status: filter.status },
				}),
			),
		approve: async (id, note) =>
			Idea.parse(
				await request<unknown>(
					baseUrl,
					`/admin/ideas/${encodeURIComponent(id)}/approve`,
					{ method: "POST", body: { note } },
				),
			),
		block: async (id, note) =>
			Idea.parse(
				await request<unknown>(baseUrl, `/admin/ideas/${encodeURIComponent(id)}/block`, {
					method: "POST",
					body: { note },
				}),
			),
		get: async (id) =>
			Idea.parse(await request<unknown>(baseUrl, `/admin/ideas/${encodeURIComponent(id)}`)),
		update: async (id, input) =>
			Idea.parse(
				await request<unknown>(baseUrl, `/admin/ideas/${encodeURIComponent(id)}`, {
					method: "PATCH",
					body: input,
				}),
			),
		remove: async (ids) => {
			await request<unknown>(baseUrl, "/admin/ideas/delete", { method: "POST", body: { ids } });
		},
		reject: async (id, reason) =>
			Idea.parse(
				await request<unknown>(
					baseUrl,
					`/admin/ideas/${encodeURIComponent(id)}/reject`,
					{ method: "POST", body: { reason } },
				),
			),
		curate: async (ids) =>
			Idea.array().parse(
				await request<unknown>(baseUrl, "/admin/ideas/curate", {
					method: "POST",
					body: { ids },
				}),
			),
		// Идея-вдохновение, созданная площадкой (автор OSMIX): наполняет ленту
		// вдохновения помимо модерации идей продавцов.
		createPlatform: async (input) =>
			Idea.parse(
				await request<unknown>(baseUrl, "/admin/ideas", {
					method: "POST",
					body: input,
				}),
			),
		// Загрузка фото для идеи-вдохновения: сырой бинарный upload → публичный URL.
		uploadPhoto: async (file: File) =>
			(await requestUpload(baseUrl, "/admin/ideas/photo", file)).url,
	},
	orders: {
		// Разбираем заказы по одному: строгий `Order.array()` ронял весь раздел
		// из-за одного заказа с непредвиденной формой (живая оплата, старая
		// запись). Такой заказ пропускаем и называем в консоли — остальные видны.
		list: async () => parseOrders(await request<unknown>(baseUrl, "/admin/orders")),
		page: async (filter) => {
			const raw = await request<ServerPage<unknown>>(baseUrl, "/admin/orders", {
				query: {
					search: filter.search?.trim() || undefined,
					from: filter.from || undefined,
					to: filter.to || undefined,
					status: filter.statuses?.length ? filter.statuses.join(",") : undefined,
					id: filter.id || undefined,
					shopId: filter.shopId || undefined,
					sort: filter.sort,
					dir: filter.sort ? filter.dir : undefined,
					page: filter.page,
					pageSize: filter.pageSize,
				},
			});
			return { ...raw, items: parseOrders(raw.items) };
		},
		statusCounts: async (query) =>
			(await request<Record<string, number>>(baseUrl, "/admin/orders/status-counts", {
				query: {
					search: query.search?.trim() || undefined,
					from: query.from || undefined,
					to: query.to || undefined,
					shopId: query.shopId || undefined,
				},
			})) as Record<string, number>,
		shops: async () =>
			(await request<{ id: string; name: string }[]>(baseUrl, "/admin/orders/shops")) as {
				id: string;
				name: string;
			}[],
		shipments: async (orderId: string) =>
			OrderShipment.array().parse(
				await request<unknown>(
					baseUrl,
					`/orders/${encodeURIComponent(orderId)}/shipments`,
				),
			),
		confirmInvoicePayment: async (orderId: string) =>
			Order.parse(
				await request<unknown>(
					baseUrl,
					`/admin/orders/${encodeURIComponent(orderId)}/confirm-invoice-payment`,
					{ method: "POST" },
				),
			),
		setStatus: async (orderId, input) =>
			Order.parse(
				await request<unknown>(
					baseUrl,
					`/admin/orders/${encodeURIComponent(orderId)}/status`,
					{ method: "POST", body: input },
				),
			),
		receipt: async (orderId: string) =>
			OrderReceipt.parse(
				await request<unknown>(baseUrl, `/orders/${encodeURIComponent(orderId)}/receipt`),
			),
	},
	returns: {
		list: async (status?: ReturnStatus) =>
			ReturnRequest.array().parse(
				await request<unknown>(baseUrl, "/admin/returns", {
					query: status ? { status } : undefined,
				}),
			),
		page: async (filter) => {
			const raw = await request<ServerPage<unknown>>(baseUrl, "/admin/returns", {
				query: {
					status: filter.status || undefined,
					search: filter.search?.trim() || undefined,
					dispute: filter.openDisputes ? "open" : undefined,
					sort: filter.sort,
					dir: filter.sort ? filter.dir : undefined,
					page: filter.page,
					pageSize: filter.pageSize,
				},
			});
			return { ...raw, items: ReturnRequest.array().parse(raw.items) };
		},
		summary: async () =>
			ReturnsSummary.parse(
				await request<unknown>(baseUrl, "/admin/returns/summary"),
			),
		resolve: async (id: string, input: ReturnResolveInput) =>
			ReturnRequest.parse(
				await request<unknown>(
					baseUrl,
					`/admin/returns/${encodeURIComponent(id)}/resolve`,
					{ method: "POST", body: input },
				),
			),
		received: async (id: string) =>
			ReturnRequest.parse(
				await request<unknown>(
					baseUrl,
					`/admin/returns/${encodeURIComponent(id)}/received`,
					{ method: "POST" },
				),
			),
		refunded: async (id: string) =>
			ReturnRequest.parse(
				await request<unknown>(
					baseUrl,
					`/admin/returns/${encodeURIComponent(id)}/refunded`,
					{ method: "POST" },
				),
			),
		get: async (id: string) =>
			ReturnDetail.parse(
				await request<unknown>(baseUrl, `/admin/returns/${encodeURIComponent(id)}`),
			),
		resolveDispute: async (id, input) =>
			ReturnRequest.parse(
				await request<unknown>(
					baseUrl,
					`/admin/returns/${encodeURIComponent(id)}/dispute/resolve`,
					{ method: "POST", body: input },
				),
			),
	},
	carts: {
		list: async () =>
			Cart.array().parse(await request<unknown>(baseUrl, "/admin/carts")),
	},
	// Marketplace storefront catalog. `/shops` is the public buyer route (no
	// admin moderation surface); `/catalog/search?shopId=` lists a shop's
	// published products. One storefront is fetched by slug.
	storefronts: {
		list: async () =>
			Storefront.array().parse(await request<unknown>(baseUrl, "/shops")),
		get: async (slug) =>
			Storefront.parse(
				await request<unknown>(baseUrl, `/shops/${encodeURIComponent(slug)}`),
			),
		products: async (shopId) => {
			const page = await request<{ data: unknown }>(
				baseUrl,
				"/catalog/search",
				{
					query: { shopId, pageSize: 100 },
				},
			);
			return Product.array().parse(page.data);
		},
		analytics: async (shopId) =>
			ShopAnalytics.parse(
				await request<unknown>(
					baseUrl,
					`/admin/shops/${encodeURIComponent(shopId)}/analytics`,
				),
			),
		pickupPoints: async (shopId) =>
			(await request<ShopPickupPoints>(
				baseUrl,
				`/admin/shops/${encodeURIComponent(shopId)}/pickup-points`,
			)) as ShopPickupPoints,
		partners: async (shopId) =>
			(await request<ShopPartners>(
				baseUrl,
				`/admin/shops/${encodeURIComponent(shopId)}/partners`,
			)) as ShopPartners,
		salesSummary: async () =>
			ShopSalesSummary.array().parse(
				await request<unknown>(baseUrl, "/admin/shops/sales-summary"),
			),
	},
	// Seller-onboarding moderation, keyed by USER id. The gateway route family
	// is still `/admin/shops/:userId/*`; only the admin client names it sellers.
	sellers: {
		list: async () =>
			SellerShop.array().parse(await request<unknown>(baseUrl, "/admin/shops")),
		get: async (userId) =>
			SellerShopDetail.parse(
				await request<unknown>(
					baseUrl,
					`/admin/shops/${encodeURIComponent(userId)}`,
				),
			),
		approve: async (userId) =>
			SellerShop.parse(
				await request<unknown>(
					baseUrl,
					`/admin/shops/${encodeURIComponent(userId)}/approve`,
					{
						method: "POST",
					},
				),
			),
		reject: async (userId, reason, opts) =>
			SellerShop.parse(
				await request<unknown>(
					baseUrl,
					`/admin/shops/${encodeURIComponent(userId)}/reject`,
					{
						method: "POST",
						body: { reason, final: opts?.final },
					},
				),
			),
		block: async (userId) =>
			SellerShop.parse(
				await request<unknown>(
					baseUrl,
					`/admin/shops/${encodeURIComponent(userId)}/block`,
					{
						method: "POST",
					},
				),
			),
		unblock: async (userId) =>
			SellerShop.parse(
				await request<unknown>(
					baseUrl,
					`/admin/shops/${encodeURIComponent(userId)}/unblock`,
					{
						method: "POST",
					},
				),
			),
		assignManager: async (userId, managerUserId) =>
			SellerShopDetail.parse(
				await request<unknown>(
					baseUrl,
					`/admin/shops/${encodeURIComponent(userId)}/manager`,
					{
						method: "PUT",
						body: { managerUserId },
					},
				),
			),
		clearManager: async (userId) =>
			SellerShopDetail.parse(
				await request<unknown>(
					baseUrl,
					`/admin/shops/${encodeURIComponent(userId)}/manager`,
					{
						method: "DELETE",
					},
				),
			),
		// Тимлид привязан по shopKey (shopId витрины ?? userId продавца) — так же,
		// как читается в детали магазина. Ответ игнорируем: деталь перечитывается.
		assignTeamLead: async (shopKey, leadUserId) => {
			await request<unknown>(
				baseUrl,
				`/admin/shops/${encodeURIComponent(shopKey)}/team-lead`,
				{
					method: "PUT",
					body: { leadUserId },
				},
			);
		},
		clearTeamLead: async (shopKey) => {
			await request<unknown>(
				baseUrl,
				`/admin/shops/${encodeURIComponent(shopKey)}/team-lead`,
				{
					method: "DELETE",
				},
			);
		},
		staff: async (userId) =>
			ShopStaff.parse(
				await request<unknown>(
					baseUrl,
					`/admin/shops/${encodeURIComponent(userId)}/staff`,
				),
			),
		updateStaffRole: async (userId, memberId, role) =>
			ShopMember.parse(
				await request<unknown>(
					baseUrl,
					`/admin/shops/${encodeURIComponent(userId)}/staff/members/${encodeURIComponent(memberId)}`,
					{
						method: "PATCH",
						body: { role },
					},
				),
			),
		removeStaffMember: async (userId, memberId) => {
			await request<null>(
				baseUrl,
				`/admin/shops/${encodeURIComponent(userId)}/staff/members/${encodeURIComponent(memberId)}`,
				{ method: "DELETE" },
			);
		},
		profileDocumentFile: async (userId, docId) =>
			requestBlob(
				baseUrl,
				`/admin/shops/${encodeURIComponent(userId)}/documents/${encodeURIComponent(docId)}/file`,
			),
		// Э7: «личное дело» продавца (миграция 044). Список, ревью и выдача файла
		// живут под /admin/sellers/* и /admin/documents/* — отдельно от онбординг-
		// документов профиля выше.
		documents: async (userId) =>
			SellerDocumentListSchema.parse(
				await request<unknown>(
					baseUrl,
					`/admin/sellers/${encodeURIComponent(userId)}/documents`,
				),
			),
		reviewDocument: async (docId, input) =>
			SellerDocumentSchema.parse(
				await request<unknown>(
					baseUrl,
					`/admin/documents/${encodeURIComponent(docId)}/review`,
					{ method: "POST", body: input },
				),
			),
		documentFile: async (userId, docId) =>
			requestBlob(
				baseUrl,
				`/admin/sellers/${encodeURIComponent(userId)}/documents/${encodeURIComponent(docId)}/file`,
			),
		verification: async (userId) =>
			SellerVerificationSchema.parse(
				await request<unknown>(
					baseUrl,
					`/admin/sellers/${encodeURIComponent(userId)}/verification`,
				),
			),
		sections: async (userId) =>
			ProfileSectionState.array().parse(
				await request<unknown>(baseUrl, `/admin/sellers/${encodeURIComponent(userId)}/sections`),
			),
		reviewSection: async (userId, section, input) =>
			ProfileSectionState.array().parse(
				await request<unknown>(
					baseUrl,
					`/admin/sellers/${encodeURIComponent(userId)}/sections/${section}/review`,
					{ method: "POST", body: input },
				),
			),
	},
	deliveryCarriers: {
		list: async () =>
			CarrierAdmin.array().parse(await request<unknown>(baseUrl, "/admin/delivery/carriers")),
		setEnabled: async (id, enabled) =>
			CarrierAdmin.parse(
				await request<unknown>(baseUrl, `/admin/delivery/carriers/${id}`, {
					method: "PATCH",
					body: { enabled },
				}),
			),
		upsertZone: async (id, city, enabled) =>
			CarrierAdmin.parse(
				await request<unknown>(baseUrl, `/admin/delivery/carriers/${id}/zones`, {
					method: "POST",
					body: { city, enabled },
				}),
			),
		removeZone: async (id, zoneId) =>
			CarrierAdmin.parse(
				await request<unknown>(
					baseUrl,
					`/admin/delivery/carriers/${id}/zones/${encodeURIComponent(zoneId)}`,
					{ method: "DELETE" },
				),
			),
		check: async (id, input) =>
			CarrierCheck.parse(
				await request<unknown>(baseUrl, `/admin/delivery/carriers/${id}/check`, {
					method: "POST",
					body: input,
				}),
			),
	},
	settings: {
		getDelivery: async () =>
			DeliveryConfig.parse(
				await request<unknown>(baseUrl, "/admin/settings/delivery"),
			),
		updateDelivery: async (patch: DeliveryConfigPatch) =>
			DeliveryConfig.parse(
				await request<unknown>(baseUrl, "/admin/settings/delivery", {
					method: "PATCH",
					body: patch,
				}),
			),
		getModeration: async () =>
			ModerationSettings.parse(await request<unknown>(baseUrl, "/admin/settings/moderation")),
		updateModeration: async (patch) =>
			ModerationSettings.parse(
				await request<unknown>(baseUrl, "/admin/settings/moderation", {
					method: "PATCH",
					body: patch,
				}),
			),
	},
	integrations: {
		overview: async () =>
			IntegrationsOverview.parse(await request<unknown>(baseUrl, "/admin/integrations")),
		check: async (key: string) =>
			IntegrationCheckResult.parse(
				await request<unknown>(baseUrl, `/admin/integrations/${encodeURIComponent(key)}/check`, {
					method: "POST",
				}),
			),
		costs: async () =>
			InfrastructureCosts.parse(await request<unknown>(baseUrl, "/admin/integrations/costs")),
		updateCosts: async (patch) =>
			InfrastructureCosts.parse(
				await request<unknown>(baseUrl, "/admin/integrations/costs", { method: "PUT", body: patch }),
			),
		setMetrikaCounter: async (counterId) =>
			request<{ counterId: string | null }>(baseUrl, "/admin/settings/metrika", {
				method: "PATCH",
				body: { counterId },
			}),
		apiKeys: async (filter = {}) =>
			request<PartnerApiKey[]>(baseUrl, "/admin/integrations/api-keys", {
				query: { shopId: filter.shopId || undefined, active: filter.active ? "true" : undefined },
			}),
		revokeApiKey: async (id) =>
			request<PartnerApiKey>(baseUrl, `/admin/integrations/api-keys/${encodeURIComponent(id)}/revoke`, {
				method: "POST",
			}),
	},
	// Канал SMS (СМС-Центр). Гард page.integrations на чтение,
	// action.integrations.manage на пробную отправку и опрос статусов.
	sms: {
		health: async (windowHours?: number) =>
			SmsChannelHealth.parse(
				await request<unknown>(baseUrl, "/admin/sms/health", {
					query: { hours: windowHours },
				}),
			),
		log: async (filter: SmsLogFilter = {}) =>
			SmsLogPage.parse(
				await request<unknown>(baseUrl, "/admin/sms/log", {
					query: {
						phone: filter.phone || undefined,
						status: filter.status && filter.status !== "all" ? filter.status : undefined,
						limit: filter.limit,
						offset: filter.offset,
					},
				}),
			),
		events: async (filter = {}) =>
			request<IntegrationEvent[]>(baseUrl, "/admin/integrations/events", {
				query: {
					provider: filter.provider && filter.provider !== "all" ? filter.provider : undefined,
					status: filter.status && filter.status !== "all" ? filter.status : undefined,
					refId: filter.refId || undefined,
					limit: filter.limit,
				},
			}),
		eventsSummary: async () =>
			request<IntegrationEventSummary[]>(baseUrl, "/admin/integrations/events/summary"),
		test: async (input: { phone: string; text?: string; dryRun?: boolean }) =>
			SmsTestResult.parse(
				await request<unknown>(baseUrl, "/admin/sms/test", { method: "POST", body: input }),
			),
		refreshStatuses: async (limit?: number) =>
			(await request<{ updated: number }>(baseUrl, "/admin/sms/refresh-statuses", {
				method: "POST",
				body: limit ? { limit } : {},
			})) ?? { updated: 0 },
	},
	email: {
		status: async (windowHours?: number) =>
			EmailChannelStatus.parse(
				await request<unknown>(baseUrl, "/admin/email/status", { query: { hours: windowHours } }),
			),
		log: async (filter: EmailLogFilter = {}) =>
			EmailLogPage.parse(
				await request<unknown>(baseUrl, "/admin/email/log", {
					query: {
						to: filter.to || undefined,
						status: filter.status && filter.status !== "all" ? filter.status : undefined,
						template: filter.template && filter.template !== "all" ? filter.template : undefined,
						limit: filter.limit,
						offset: filter.offset,
					},
				}),
			),
		message: async (id: string) =>
			EmailMessageDetail.parse(
				await request<unknown>(baseUrl, `/admin/email/log/${encodeURIComponent(id)}`),
			),
		retry: async (id: string) =>
			EmailSendOutcome.parse(
				await request<unknown>(baseUrl, `/admin/email/log/${encodeURIComponent(id)}/retry`, {
					method: "POST",
					body: {},
				}),
			),
		test: async (to: string) =>
			EmailSendOutcome.parse(
				await request<unknown>(baseUrl, "/admin/email/test", { method: "POST", body: { to } }),
			),
		resume: async () => {
			await request<unknown>(baseUrl, "/admin/email/resume", { method: "POST", body: {} });
		},
		templates: async () =>
			EmailTemplateMeta.array().parse(await request<unknown>(baseUrl, "/admin/email/templates")),
		preview: async (id: string) =>
			EmailTemplatePreview.parse(
				await request<unknown>(baseUrl, `/admin/email/templates/${encodeURIComponent(id)}/preview`),
			),
	},
	geo: {
		listCountries: async () =>
			GeoCountry.array().parse(await request<unknown>(baseUrl, "/admin/geo/countries")),
		updateCountries: async (countries: GeoCountry[]) =>
			GeoCountry.array().parse(
				await request<unknown>(baseUrl, "/admin/geo/countries", {
					method: "PATCH",
					body: { countries },
				}),
			),
		listCurrencies: async () =>
			GeoCurrency.array().parse(await request<unknown>(baseUrl, "/admin/geo/currencies")),
		updateCurrencies: async (currencies: GeoCurrency[]) =>
			GeoCurrency.array().parse(
				await request<unknown>(baseUrl, "/admin/geo/currencies", {
					method: "PATCH",
					body: { currencies },
				}),
			),
	},
	// Геоаналитика потоков площадки: ГДЕ продавцы vs КУДА заказы + потоки между
	// регионами. `?days=N` трактуется бэкендом как пресет-окно.
	analytics: {
		geoFlows: async (input) =>
			GeoFlowAnalyticsSchema.parse(
				await request<unknown>(baseUrl, "/admin/analytics/geo-flows", {
					query: { days: input.days, limit: input.limit },
				}),
			),
		metrika: async (days) =>
			await request<MetrikaReport>(baseUrl, "/admin/analytics/metrika", { query: { days } }),
		ensureMetrikaGoals: async () =>
			await request<{ created: string[]; existing: string[] }>(baseUrl, "/admin/analytics/metrika/goals", {
				method: "POST",
			}),
	},
	// The founder workspace is now gateway-backed: /admin/dashboard/overview,
	// /admin/finances/summary and /admin/applications/* return the same shapes
	// the mock provider used to synthesise, computed from the database instead.
	// Keep the mock around for offline UI work — see BUN_PUBLIC_MOCK_DOMAINS.
	dashboard: {
		overview: async (range) =>
			(await request<DashboardOverview>(baseUrl, "/admin/dashboard/overview", {
				query: rangeQuery(range),
			})) as DashboardOverview,
	},
	finances: {
		summary: async (range) =>
			(await request<FinanceSummary>(baseUrl, "/admin/finances/summary", {
				query: rangeQuery(range),
			})) as FinanceSummary,
		getSettings: async () =>
			FinanceSettings.parse(await request<unknown>(baseUrl, "/admin/finances/settings")),
		updateSettings: async (patch) =>
			FinanceSettings.parse(
				await request<unknown>(baseUrl, "/admin/finances/settings", {
					method: "PATCH",
					body: patch,
				}),
			),
		payouts: async (status = "all") =>
			PayoutRequest.array().parse(
				await request<unknown>(baseUrl, `/admin/payouts?status=${encodeURIComponent(status)}`),
			),
		payoutPage: async (status, page, pageSize) => {
			const raw = await request<ServerPage<unknown>>(baseUrl, "/admin/payouts", {
				query: { status, page, pageSize },
			});
			return { ...raw, items: PayoutRequest.array().parse(raw.items) };
		},
		payoutDetails: async (id) =>
			PayoutDetails.parse(
				await request<unknown>(baseUrl, `/admin/payouts/${encodeURIComponent(id)}`),
			),
		decidePayout: async (id, input) =>
			PayoutRequest.parse(
				await request<unknown>(
					baseUrl,
					`/admin/payouts/${encodeURIComponent(id)}/decision`,
					{ method: "POST", body: input },
				),
			),
		payoutRegistry: async () =>
			(await request<PaymentRegistry>(baseUrl, "/admin/payouts/registry")) as PaymentRegistry,
		exportPayoutRegistry: (format) =>
			requestFile(
				baseUrl,
				`/admin/payouts/registry/export?format=${format}`,
				format === "1c" ? "OSMIX платёжные поручения.txt" : "OSMIX реестр выплат.xlsx",
			),
		cashFlow: async (from, to) =>
			(await request<CashFlowReport>(baseUrl, "/admin/finances/cash-flow", {
				query: { from, to },
			})) as CashFlowReport,
		exportCashFlow: (from, to, format) =>
			requestFile(
				baseUrl,
				`/admin/finances/cash-flow/export?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&format=${format}`,
				`OSMIX ДДС.${format}`,
			),
		accountingShops: async () =>
			(await request<AccountingShop[]>(baseUrl, "/admin/accounting/shops")) as AccountingShop[],
		accountingSales: async (query) =>
			(await request<AccountingSalesSummary>(baseUrl, "/admin/accounting/sales", {
				query: accountingQuery(query),
			})) as AccountingSalesSummary,
		accountingReturns: async (query) =>
			(await request<AccountingReturnsSummary>(baseUrl, "/admin/accounting/returns", {
				query: accountingQuery(query),
			})) as AccountingReturnsSummary,
		agentReports: async (query) =>
			(await request<AgentReportSummary[]>(baseUrl, "/admin/accounting/agent-reports", {
				query: accountingQuery(query),
			})) as AgentReportSummary[],
		commissionActs: async (query) =>
			(await request<CommissionActSummary[]>(baseUrl, "/admin/accounting/commission-acts", {
				query: accountingQuery(query),
			})) as CommissionActSummary[],
		settlements: async (query) =>
			(await request<SettlementsReport>(baseUrl, "/admin/accounting/settlements", {
				query: accountingQuery(query),
			})) as SettlementsReport,
		accountingJournal: async () =>
			(await request<AccountingJournalEntry[]>(baseUrl, "/admin/accounting/journal")) as AccountingJournalEntry[],
		exportAccounting: (dataset, query, format) => {
			const params = new URLSearchParams({ ...accountingQuery(query), format });
			return requestFile(
				baseUrl,
				`/admin/accounting/${dataset}/export?${params.toString()}`,
				`OSMIX ${dataset}.${format}`,
			);
		},
		advanceObligation: async (id, state) =>
			(await request<{ id: string; state: ObligationState }>(
				baseUrl,
				`/admin/finances/obligations/${id}/advance`,
				{ method: "POST", body: { state } },
			)) as { id: string; state: ObligationState },
		generateDocuments: async (id) =>
			(await request<ObligationDocument[]>(
				baseUrl,
				`/admin/finances/obligations/${id}/documents`,
				{ method: "POST" },
			)) as ObligationDocument[],
	},
	commissions: {
		overview: async (days) =>
			CommissionOverview.parse(
				await request<unknown>(baseUrl, "/admin/commissions", { query: { days } }),
			),
		shops: async (days) =>
			CommissionShopList.parse(
				await request<unknown>(baseUrl, "/admin/commissions/shops", { query: { days } }),
			),
		categoryShops: async (categoryId, days) =>
			CommissionCategoryShops.parse(
				await request<unknown>(
					baseUrl,
					`/admin/commissions/categories/${encodeURIComponent(categoryId)}/shops`,
					{ query: { days } },
				),
			),
		history: async (filter = {}) =>
			CommissionHistoryEntry.array().parse(
				await request<unknown>(baseUrl, "/admin/commissions/history", {
					query: { shopId: filter.shopId, categoryId: filter.categoryId, limit: filter.limit },
				}),
			),
		setBase: async (rate) =>
			(await request<{ baseRate: number }>(baseUrl, "/admin/commissions/base", {
				method: "PUT",
				body: { rate },
			})) as { baseRate: number },
		setRate: async (change) =>
			(await request<{ changed: number }>(baseUrl, "/admin/commissions/rates", {
				method: "PUT",
				body: change,
			})) as { changed: number },
		bulk: async (input) =>
			(await request<{ changed: number }>(baseUrl, "/admin/commissions/rates/bulk", {
				method: "POST",
				body: input,
			})) as { changed: number },
	},
	promos: {
		list: async () => (await request<PromoCode[]>(baseUrl, "/admin/promos")) as PromoCode[],
		create: async (input: PromoCodeInput) =>
			(await request<PromoCode>(baseUrl, "/admin/promos", { method: "POST", body: input })) as PromoCode,
		update: async (code: string, patch: Partial<PromoCodeInput> & { archived?: boolean }) =>
			(await request<PromoCode>(baseUrl, `/admin/promos/${encodeURIComponent(code)}`, {
				method: "PATCH",
				body: patch,
			})) as PromoCode,
		remove: async (code: string) => {
			await request<{ ok: boolean }>(baseUrl, `/admin/promos/${encodeURIComponent(code)}`, {
				method: "DELETE",
			});
		},
		get: async (code) =>
			(await request<PromoCode>(baseUrl, `/admin/promos/${encodeURIComponent(code)}`)) as PromoCode,
		stats: async (code, period = {}) =>
			(await request<PromoStats>(baseUrl, `/admin/promos/${encodeURIComponent(code)}/stats`, {
				query: { from: period.from || undefined, to: period.to || undefined },
			})) as PromoStats,
		exportStats: (code, period, format) => {
			const params = new URLSearchParams({ format });
			if (period.from) params.set("from", period.from);
			if (period.to) params.set("to", period.to);
			return requestFile(
				baseUrl,
				`/admin/promos/${encodeURIComponent(code)}/stats/export?${params.toString()}`,
				`Промокод ${code}.${format}`,
			);
		},
		check: async (code, audience) =>
			(await request<PromoCheckResult>(baseUrl, "/promos/check", { query: { code, audience } })) as PromoCheckResult,
		settings: async () => (await request<PartnerSettings>(baseUrl, "/admin/promo-settings")) as PartnerSettings,
		updateSettings: async (input) =>
			(await request<PartnerSettings>(baseUrl, "/admin/promo-settings", { method: "PUT", body: input })) as PartnerSettings,
		sellerBinding: async (userId) =>
			(await request<SellerPartnerPromo>(
				baseUrl,
				`/admin/sellers/${encodeURIComponent(userId)}/partner-promo`,
			)) as SellerPartnerPromo,
		bindSeller: async (userId, promo, reason) =>
			(await request<SellerPartnerPromo>(baseUrl, `/admin/sellers/${encodeURIComponent(userId)}/partner-promo`, {
				method: "PUT",
				body: { promo, reason },
			})) as SellerPartnerPromo,
		personalOffers: async () =>
			(await request<PersonalOffersOverview>(baseUrl, "/admin/personal-offers")) as PersonalOffersOverview,
		updatePersonalOffers: async (input) =>
			(await request<PersonalOffersSettings>(baseUrl, "/admin/personal-offers/settings", {
				method: "PUT",
				body: input,
			})) as PersonalOffersSettings,
		runPersonalOffers: async () =>
			(await request<PersonalOffersRunResult>(baseUrl, "/admin/personal-offers/run", {
				method: "POST",
			})) as PersonalOffersRunResult,
	},
	partners: {
		list: async (search) =>
			(await request<Partner[]>(baseUrl, "/admin/partners", { query: { search: search || undefined } })) as Partner[],
		detail: async (id, period = {}) =>
			(await request<PartnerDetail>(baseUrl, `/admin/partners/${encodeURIComponent(id)}`, {
				query: { from: period.from || undefined, to: period.to || undefined },
			})) as PartnerDetail,
		create: async (input) =>
			(await request<Partner>(baseUrl, "/admin/partners", { method: "POST", body: input })) as Partner,
		update: async (id, patch) =>
			(await request<Partner>(baseUrl, `/admin/partners/${encodeURIComponent(id)}`, {
				method: "PATCH",
				body: patch,
			})) as Partner,
		remove: async (id) => {
			await request<{ ok: boolean }>(baseUrl, `/admin/partners/${encodeURIComponent(id)}`, { method: "DELETE" });
		},
	},
	applications: {
		board: async () => (await request<ApplicationBoard>(baseUrl, "/admin/applications/board")) as ApplicationBoard,
		move: async (id, status, reason) =>
			(await request<SellerApplication>(baseUrl, `/admin/applications/${id}/move`, {
				method: "POST",
				body: { status, reason },
			})) as SellerApplication,
		assign: async (id, assigneeId) =>
			(await request<SellerApplication>(baseUrl, `/admin/applications/${id}/assign`, {
				method: "POST",
				body: { assigneeId },
			})) as SellerApplication,
		rework: async (id, input: ApplicationReworkInput) =>
			(await request<ApplicationReworkResult>(baseUrl, `/admin/applications/${id}/rework`, {
				method: "POST",
				body: { message: input.message, deadline: input.deadline, docType: input.docType },
			})) as ApplicationReworkResult,
		createAccount: async (id, input) =>
			(await request<ApplicationAccountResult>(
				baseUrl,
				`/admin/applications/${encodeURIComponent(id)}/create-account`,
				{ method: "POST", body: input },
			)) as ApplicationAccountResult,
	},
	reports: {
		getModel: async () =>
			(
				(await request<{ model: string }>(baseUrl, "/admin/settings/reports-model")) as {
					model: string
				}
			).model,
		setModel: async (model: string) =>
			(
				(await request<{ model: string }>(baseUrl, "/admin/settings/reports-model", {
					method: "PATCH",
					body: { model },
				})) as { model: string }
			).model,
		catalog: async () =>
			ReportCatalogEntry.array().parse(await request<unknown>(baseUrl, "/admin/reports/catalog")),
		generate: async (type, days) =>
			GeneratedReport.parse(
				await request<unknown>(baseUrl, "/admin/reports/generate", {
					method: "POST",
					body: { type, days },
				}),
			),
		history: async () =>
			ReportHistoryEntry.array().parse(await request<unknown>(baseUrl, "/admin/reports")),
		// HTML route returns a raw document (not a JSON envelope) and needs the
		// bearer token — fetched as text so the viewer can render it via srcDoc.
		getHtml: async (id) => requestText(baseUrl, `/admin/reports/${encodeURIComponent(id)}/html`),
	},
	// Центр помощи (Э5, миграция 045). Все /admin/support/* — под правом
	// page.support (роль admin проверку обходит). Списки приходят в конверте
	// `{ items }`, поэтому распаковываем поле items до массива.
	support: {
		faq: async () => {
			const data = await request<{ items: unknown }>(baseUrl, "/admin/support/faq");
			return FaqArticleSchema.array().parse(data.items);
		},
		createFaq: async (input) =>
			FaqArticleSchema.parse(
				await request<unknown>(baseUrl, "/admin/support/faq", {
					method: "POST",
					body: input,
				}),
			),
		updateFaq: async (id, input) =>
			FaqArticleSchema.parse(
				await request<unknown>(baseUrl, `/admin/support/faq/${encodeURIComponent(id)}`, {
					method: "PATCH",
					body: input,
				}),
			),
		deleteFaq: async (id) => {
			await request<unknown>(baseUrl, `/admin/support/faq/${encodeURIComponent(id)}`, {
				method: "DELETE",
			});
		},
		uploadAttachment: async (file: File) => {
			const res = (await requestUpload(baseUrl, "/support/attachments", file)) as { url: string; name?: string };
			return {
				url: res.url,
				name: res.name || file.name,
				kind: file.type.startsWith("image/") ? ("image" as const) : ("file" as const),
			};
		},
		tickets: async (status) => {
			const data = await request<{ items: unknown }>(baseUrl, "/admin/support/tickets", {
				query: status ? { status } : undefined,
			});
			return SupportTicketSchema.array().parse(data.items);
		},
		ticket: async (id) =>
			SupportTicketDetailSchema.parse(
				await request<unknown>(baseUrl, `/admin/support/tickets/${encodeURIComponent(id)}`),
			),
		reply: async (id, input) =>
			SupportTicketDetailSchema.parse(
				await request<unknown>(baseUrl, `/admin/support/tickets/${encodeURIComponent(id)}/reply`, {
					method: "POST",
					body: input,
				}),
			),
		setStatus: async (id, status) =>
			SupportTicketDetailSchema.parse(
				await request<unknown>(baseUrl, `/admin/support/tickets/${encodeURIComponent(id)}/status`, {
					method: "POST",
					body: { status },
				}),
			),
	},
	applicationCrm: {
		card: async (id) => request<ApplicationCard>(baseUrl, `/admin/applications/${encodeURIComponent(id)}/card`),
		create: async (input) =>
			request<ApplicationCard>(baseUrl, "/admin/applications", { method: "POST", body: input }),
		duplicates: async (query) =>
			request<CrmDuplicate[]>(baseUrl, "/admin/applications/duplicates", { query }),
		update: async (id, patch) =>
			request<ApplicationCard>(baseUrl, `/admin/applications/${encodeURIComponent(id)}/profile`, {
				method: "PATCH",
				body: patch,
			}),
		fillCompany: async (id) =>
			request<ApplicationCard>(baseUrl, `/admin/applications/${encodeURIComponent(id)}/company/fill`, {
				method: "POST",
			}),
		addContact: async (id, input) =>
			request<CrmContact>(baseUrl, `/admin/applications/${encodeURIComponent(id)}/contacts`, {
				method: "POST",
				body: input,
			}),
		updateContact: async (id, contactId, patch) =>
			request<CrmContact>(
				baseUrl,
				`/admin/applications/${encodeURIComponent(id)}/contacts/${encodeURIComponent(contactId)}`,
				{ method: "PATCH", body: patch },
			),
		deleteContact: async (id, contactId) => {
			await request<void>(
				baseUrl,
				`/admin/applications/${encodeURIComponent(id)}/contacts/${encodeURIComponent(contactId)}`,
				{ method: "DELETE" },
			);
		},
		addActivity: async (id, input) =>
			request<ApplicationCard>(baseUrl, `/admin/applications/${encodeURIComponent(id)}/activities`, {
				method: "POST",
				body: input,
			}),
		updateActivity: async (id, activityId, patch) =>
			request<CrmActivity>(
				baseUrl,
				`/admin/applications/${encodeURIComponent(id)}/activities/${encodeURIComponent(activityId)}`,
				{ method: "PATCH", body: patch },
			),
		deleteActivity: async (id, activityId) => {
			await request<void>(
				baseUrl,
				`/admin/applications/${encodeURIComponent(id)}/activities/${encodeURIComponent(activityId)}`,
				{ method: "DELETE" },
			);
		},
		addTask: async (id, input) =>
			request<CrmTask>(baseUrl, `/admin/applications/${encodeURIComponent(id)}/tasks`, {
				method: "POST",
				body: input,
			}),
		updateTask: async (id, taskId, patch) =>
			request<CrmTask>(
				baseUrl,
				`/admin/applications/${encodeURIComponent(id)}/tasks/${encodeURIComponent(taskId)}`,
				{ method: "PATCH", body: patch },
			),
		deleteTask: async (id, taskId) => {
			await request<void>(
				baseUrl,
				`/admin/applications/${encodeURIComponent(id)}/tasks/${encodeURIComponent(taskId)}`,
				{ method: "DELETE" },
			);
		},
		tasks: async (scope, due) =>
			request<CrmTaskWithLead[]>(baseUrl, "/admin/applications/tasks", { query: { scope, due } }),
		uploadFile: async (id, file, category) =>
			requestForm<CrmFile>(
				baseUrl,
				`/admin/applications/${encodeURIComponent(id)}/files`,
				file,
				category ? { category } : {},
			),
		fileBlob: async (id, fileId) =>
			requestBlob(
				baseUrl,
				`/admin/applications/${encodeURIComponent(id)}/files/${encodeURIComponent(fileId)}?inline=1`,
			),
		updateFile: async (id, fileId, patch) =>
			request<CrmFile>(
				baseUrl,
				`/admin/applications/${encodeURIComponent(id)}/files/${encodeURIComponent(fileId)}`,
				{ method: "PATCH", body: patch },
			),
		deleteFile: async (id, fileId) => {
			await request<void>(
				baseUrl,
				`/admin/applications/${encodeURIComponent(id)}/files/${encodeURIComponent(fileId)}`,
				{ method: "DELETE" },
			);
		},
		importLeads: async (input) =>
			request<CrmLeadImportResult>(baseUrl, "/admin/applications/import", { method: "POST", body: input }),
		exportLeads: async () => {
			const file = await requestFile(baseUrl, "/admin/applications/export.xlsx", "Заявки продавцов OSMIX.xlsx");
			return { blob: file.blob, filename: file.filename, rows: file.rows };
		},
	},
	catalogImport: {
		jobs: async (query = {}) => request<CatalogImportJob[]>(baseUrl, "/admin/catalog-import/jobs", { query }),
		create: async (input) =>
			request<CatalogImportJob>(baseUrl, "/admin/catalog-import/jobs", { method: "POST", body: input }),
		job: async (id) =>
			request<CatalogImportJobDetail>(baseUrl, `/admin/catalog-import/jobs/${encodeURIComponent(id)}`),
		update: async (id, patch) =>
			request<CatalogImportJob>(baseUrl, `/admin/catalog-import/jobs/${encodeURIComponent(id)}`, {
				method: "PATCH",
				body: patch,
			}),
		cancel: async (id) => {
			await request<void>(baseUrl, `/admin/catalog-import/jobs/${encodeURIComponent(id)}`, { method: "DELETE" });
		},
		uploadFile: async (id, file, path) =>
			requestForm<CatalogImportFile[]>(
				baseUrl,
				`/admin/catalog-import/jobs/${encodeURIComponent(id)}/files`,
				file,
				path ? { path } : {},
			),
		addText: async (id, input) =>
			request<CatalogImportFile>(baseUrl, `/admin/catalog-import/jobs/${encodeURIComponent(id)}/text`, {
				method: "POST",
				body: input,
			}),
		addFromApplication: async (id, input) =>
			request<CatalogImportFile[]>(
				baseUrl,
				`/admin/catalog-import/jobs/${encodeURIComponent(id)}/files/from-application`,
				{ method: "POST", body: input },
			),
		deleteFile: async (id, fileId) => {
			await request<void>(
				baseUrl,
				`/admin/catalog-import/jobs/${encodeURIComponent(id)}/files/${encodeURIComponent(fileId)}`,
				{ method: "DELETE" },
			);
		},
		fileBlob: async (id, fileId) =>
			requestBlob(
				baseUrl,
				`/admin/catalog-import/jobs/${encodeURIComponent(id)}/files/${encodeURIComponent(fileId)}/content?inline=1`,
			),
		parse: async (id) =>
			request<CatalogImportJob>(baseUrl, `/admin/catalog-import/jobs/${encodeURIComponent(id)}/parse`, {
				method: "POST",
			}),
		updateItem: async (id, itemId, patch) =>
			request<CatalogImportItem>(
				baseUrl,
				`/admin/catalog-import/jobs/${encodeURIComponent(id)}/items/${encodeURIComponent(itemId)}`,
				{ method: "PATCH", body: patch },
			),
		bulkUpdate: async (id, input) =>
			request<{ updated: number }>(baseUrl, `/admin/catalog-import/jobs/${encodeURIComponent(id)}/items/bulk`, {
				method: "POST",
				body: input,
			}),
		regenerateItem: async (id, itemId, input) =>
			request<CatalogImportItem>(
				baseUrl,
				`/admin/catalog-import/jobs/${encodeURIComponent(id)}/items/${encodeURIComponent(itemId)}/regenerate`,
				{ method: "POST", body: input },
			),
		commit: async (id, input = {}) =>
			request<CatalogImportJob>(baseUrl, `/admin/catalog-import/jobs/${encodeURIComponent(id)}/commit`, {
				method: "POST",
				body: input,
			}),
		template: async () => {
			const file = await requestFile(
				baseUrl,
				"/admin/catalog-import/template.xlsx",
				"Шаблон загрузки каталога OSMIX.xlsx",
			);
			return { blob: file.blob, filename: file.filename };
		},
		models: async () => request<CatalogImportModels>(baseUrl, "/admin/catalog-import/models"),
	},
	// Журнал действий консоли. Фильтры уходят как query — пустые значения
	// отбрасывает сам `request`, поэтому чистить объект здесь не нужно.
	audit: {
		list: async (filter = {}) =>
			request<AuditPage>(baseUrl, "/admin/audit", {
				query: {
					actorUserId: filter.actorUserId,
					entityKind: filter.entityKind,
					entityId: filter.entityId,
					action: filter.action,
					q: filter.q,
					from: filter.from,
					to: filter.to,
					page: filter.page,
					pageSize: filter.pageSize,
				},
			}),
		facets: async () => request<AuditFacets>(baseUrl, "/admin/audit/facets"),
		forEntity: async (kind, id) =>
			request<AuditEntry[]>(
				baseUrl,
				`/admin/audit/entity/${encodeURIComponent(kind)}/${encodeURIComponent(id)}`,
			),
	},
});
